/* 
 *  Copyright (c) Intel Corporation.  All Rights Reserved.
 *
 *  Primary entry point for transmitting data to endpoints
 */
/* --!!!!!ATTENTION!!!!!----- DO NOT DELETE code inside Webpack Macros, it will be used in Analytics SDK for Web */

// #IFDEF WebPack

import { ModuleManager , StorageManager , globalEngine } from "./Init";
import { logNormal , logInformation , logWarning , logError, logDebug, logConsole , logRawTrack , logTransports , logAutomationError} from "./bootstrap_web_ext";
import { dataManipulator , isoDateToDate } from "./common";
import { engine } from "./engine";

// #ENDIF WebPack
function CreateAnalyticsTransport() {
    function Transmitter() {
        this.retrieveStoredQueue();
    };

    Transmitter.prototype = ModuleManager.create("transmitter_template");

    Transmitter.prototype.transmit = function (messageName, message, profileNames, dataSetNames) {
        logDebug("analyticstransport.transmit message=" + JSON.stringify(message) + ", profileNames=" + JSON.stringify(profileNames) + ", datasetNames=" + JSON.stringify(dataSetNames));

        // event-level throttle
        if (this._isEventThrottled(messageName)) {
            logDebug("Event " + messageName + " was event-level throttled");
            logAutomationError(messageName,
                JSON.stringify(message),
                JSON.stringify({
                    "level": "info",
                    "type": { "eventThrottled": messageName + " is event throttled" }
                }));
            return;
        }

        for (var index in profileNames) {
            try {

                var profileName = profileNames[index];
                // Profile-level throttle
                // Don't throttle for critical events
                var eventConfigManager = ModuleManager.getSingleton("config_manager");
                var eventPriority = eventConfigManager.getEventPriority(messageName);
                var isPriorityCritical = (eventPriority == 'Critical') || (eventPriority == 'critical');
                if (this._isProfileThrottled(messageName, profileName) && !isPriorityCritical) 
                {
                    logDebug("Event " + messageName + " was profile-level throttled by '" + profileName + "'");
                    logAutomationError(messageName,
                        JSON.stringify(message),
                        JSON.stringify({
                            "level": "info",
                            "type": { "profileThrottled": messageName + " is profile throttled for " + profileName }
                        }));
                    continue;
                }

                var emitter = this.retrieveEmitter(profileName);
                if (!emitter) {
                    // we're in serious trouble here!
                    logError("Failed to locate an emitter for '" + profileName + "'; aborting send");
                    continue;
                }

                // now create the outbound record, deep copies message to outbound for local scope modifications
                var outbound = {};
                if (false === emitter.transportConfiguration["forceLowerCaseKeys"]) {
                    //To be backwards compatible with the data aggregator, all keys need to remain as is.
                    dataManipulator.mergeDictionary(outbound, message);
                }
                else {
                    dataManipulator.mergeAndLowerDictionary(outbound, message);
                }

                // apply the first and second layers of mappings
                var mappings = ModuleManager.getSingleton("mappings");
                outbound = mappings.eventMap(outbound, messageName);
                outbound = mappings.globalMap(outbound);
                outbound = mappings.toLowerCase(outbound, true, false);

                //apply attribute rules
                var rulesApplied = this._applyAttributeRules(outbound, messageName, profileName);
                outbound = rulesApplied.message;

                //send rule failure events
                if (rulesApplied.error) {
                    var errorTransport = ModuleManager.getSingleton("error_transmitter");
                    errorTransport.transmit(messageName, message, rulesApplied.error);
                }

                //add event day index
                var rules = ModuleManager.getSingleton("rules");
                var eventDayIndex = rules.getDailyMaxTableCounter(profileName);
                outbound['__event.day.index'] = eventDayIndex;

                //add throttle
                outbound['__throttle_user_multiplier'] = emitter.throttleMultiplier ? emitter.throttleMultiplier : 1;

                // Add default elements (UserID, contentVersion, etc)
                dataManipulator.mergeAndLowerDictionary(outbound, engine.getDefaultDictionary());

                // TODO: add lower case support in C++ plugins                    
                // C++ plugins (CSP, GA) log the event by looking for Hit.UniqueID
                outbound["Hit.UniqueID"] = messageName;

                //add dataset names
                this.addDataSetNames(outbound, emitter, dataSetNames);

                // Add queue time
                var now_time = new Date();
                var timeStamp = unescape(outbound["__record.created"]);
                timeStamp = timeStamp.split(" ").join('T');

                var create_time = isoDateToDate(timeStamp);
                if (now_time && create_time) {
                    var queue_time_milliseconds = (now_time - create_time);
                    outbound["__queue.time.milliseconds"] = String(queue_time_milliseconds);
                }

                logDebug("ready to send " + JSON.stringify(outbound));
                var result = emitter.send(outbound);

                logDebug('ProfileName:' + profileName + '. Result: ' + result);
                logDebug("******************************* done");

                if ('csp' == emitter.transportName) continue;  // Don't enqueue for csp transport
                if ('msgbus' == emitter.transportName) continue;  // Don't enqueue for msgbus transport

                if (!emitter._isEnabled()) continue;           // Don't enqueue if transport is disabled

                var priority = outbound['priority'];// TODO: placeholder until changes for event priority take place
                if (!result) {
                    // Sending failed, queue event and try later
                    this._addToQueue(profileName, outbound, priority);
                }
                else {
                    // Send to one transport was successfull, try sending pending messages
                    this.processQueues();
                }

            }
            catch (e) {
                logError("Failed to send mesage to '" + profileName + "': exception is '" + e.message + "'");
            }
        }
    };
    Transmitter.prototype.notifyHostTeardown = function () {
        try {
            if (!this._transportsQueue) { return; }
            var criticalQueue = this._transportsQueue['Critical'];
            if (!criticalQueue) {
                logInformation('transport::notifyHostTeardown: Critical queue is null');
                return;
            }

            var queueStr = JSON.stringify(criticalQueue);
            if (queueStr === '{}') {
                logInformation('transport::notifyHostTeardown: Nothing critical to store');
                return;
            }
            StorageManager.set('analytics_transports_transportsQueue', queueStr, false);
        }
        catch (e) {
            logError('transport::notifyHostTeardown: ' + e.message);
        }
    };
    Transmitter.prototype.retrieveStoredQueue = function () {
        try {
            var value = StorageManager.get('analytics_transports_transportsQueue');
            var valueObj = JSON.parse(value);
            this._transportsQueue['Critical'] = valueObj ? valueObj : {};
            StorageManager.remove('analytics_transports_transportsQueue');
            this._queueEmpty = (JSON.stringify(this._transportsQueue['Critical']) === '{}') ? true : false;
        }
        catch (e) {
            logError('transport::retrieveStoredQueue: ' + e.message);
        }
    };
    Transmitter.prototype.processQueues = function () {
        try {
            if (this._queueEmpty) {
                // Nothing to process
                return;
            }

            var retry = false;
            logInformation('transport:processQueues Start');

            this._queueProcessingTimeoutId = null;
            for (var priority in this._transportsQueue) {
                for (var profileName in this._transportsQueue[priority]) {
                    logInformation('transport.processQueues Processing queue [' + profileName + '][' + priority + ']');

                    var queue = this._transportsQueue[priority][profileName];
                    if (!queue) {
                        continue;
                    }
                    var emitter = this.retrieveEmitter(profileName);
                    queue = this._processSingleQueue(emitter, queue);
                    if (queue.length === 0) {
                        // Delete entry from queue
                        delete this._transportsQueue[priority][profileName];
                        logInformation('transport.processQueues Queue [' + profileName + '][' + priority + '] is now empty');
                    }
                    else {
                        this._transportsQueue[priority][profileName] = queue;
                        // Atlease one event failed, try sending it later
                        retry = true;
                    }
                }
            }

            if (retry) {
                this._timeoutInterval += 30 * 1000;
                this._setQueueProcessingTimeout();
            }
            else {
                this._queueEmpty = true;
                this._timeoutInterval = 30 * 1000;
            }
        }
        catch (e) {
            logError('transport:processQueues: ' + e.message);
        }
    };
    Transmitter.prototype._processSingleQueue = function (emitter, queue) {
        while (queue.length) {
            var outbound = queue.shift();
            if (!emitter.send(outbound)) {
                //Send failed again, place it back in queue
                // TODO: is it possible a malformed message to always fail?
                queue.unshift(outbound);
                break;
            }
        }
        logInformation('_processSingleQueue: End [' + emitter.profileName + ']queue.length: ' + queue.length);
        return queue;
    };
    Transmitter.prototype._addToQueue = function (profileName, outbound, priority) {
        if (!this._transportsQueue[priority]) { return; } // Not supported priority

        logInformation('transport::_addToQueue: Adding to ' + profileName + ' queue.');
        if (!this._transportsQueue[priority][profileName]) {
            this._transportsQueue[priority][profileName] = [];
        }
        this._transportsQueue[priority][profileName].push(outbound);
        this._queueEmpty = false;
        this._setQueueProcessingTimeout();
    };
    Transmitter.prototype._setQueueProcessingTimeout = function () {
        if (this._queueProcessingTimeoutId) return;
        this._queueProcessingTimeoutId = setTimeout(function () {
            var transport = ModuleManager.getSingleton("transport");
            transport.processQueues.apply(transport);
        }, this._timeoutInterval);
    };
    Transmitter.prototype._applyAttributeRules = function (message, messageName, profileName) {
        try {
            var eventConfigManager = ModuleManager.getSingleton("config_manager");
            var attributeRules = eventConfigManager.getAttributeRules(messageName);
            if (!attributeRules) {
                return message;
            }

            // deep copy the attribute rules and then map them
            var mappedRules = {};
            dataManipulator.mergeDictionary(mappedRules, attributeRules);

            // normalizing the rules to post global mappings and then apply the rules after normalization
            // message should already be normalized prior to reaching here
            var mappings = ModuleManager.getSingleton("mappings");
            mappedRules = mappings.eventMap(mappedRules, messageName);
            mappedRules = mappings.globalMap(mappedRules);
            mappedRules = mappings.toLowerCase(mappedRules, true, false);

            var rules = ModuleManager.getSingleton("rules");

            // Hit.UniqueID was reference incorrectly as message.Hit.UniqueID and fixed recently, broken between 02/24 - 04/03
            var defaults = ['uniqueid',
                'hit.uniqueid',
                'event.category',
                'event.action',
                'tracker.type',
                '__transport.name',
                '__record.created',
                '__caller.name'];

            defaults = defaults.concat(this._getProfileDefaultAttributes(profileName));

            var cleanMessage = {}; // Variable to contain cleansed data

            var mismatched = [];
            var ruleError = [];

            // ignoring strict attribute rule implementation for profile_csis_ga
            if (this._getProfile(profileName)["ignoreAttributeRules"] == true) {
                //fixed shallow copy object to deepcopy
                //For error/automation logging, it should be a deepcopy
                cleanMessage = JSON.parse(JSON.stringify(message));

            } else {
                // Create a new object (cleanMessage) that includes default and rule defined items only
                for (var ruleIndex in message) {
                    // check if attribute is default
                    var isDefault = false;
                    for (var idx in defaults) {
                        if (ruleIndex == defaults[idx]) {
                            isDefault = true;
                            break;
                        }
                    }

                    if (isDefault || mappedRules.hasOwnProperty(ruleIndex)) {
                        cleanMessage[ruleIndex] = message[ruleIndex];
                    }
                    // Ignoring attributes that starts with __ (sdk added metadata)
                    // assuming none of the attributes in the portal starts with __ (which is true at this time)
                    else if (ruleIndex.substring(0, 2) != "__") {
                        cleanMessage[ruleIndex] = "[ruleMismatch]";
                        // Attributes that are not configured in the portal
                        mismatched.push({
                            "attribute": ruleIndex,
                            "value": message[ruleIndex], "rule": "attribute not configured"
                        });
                    }
                }
            }

            logInformation("cleanMessage: " + JSON.stringify(cleanMessage));

            // Apply event rules
            for (var ruleIndex in mappedRules) {
                var rule = mappedRules[ruleIndex];
                if (rule.optional && (cleanMessage[ruleIndex] == null)) {
                    logInformation('_applyAttributeRules: optional field ' + ruleIndex + ' was skipped');
                    continue;
                }

                var ruleApplied = rules.apply(cleanMessage[ruleIndex], rule);
                cleanMessage[ruleIndex] = ruleApplied;

                //Attribute configured as not optional and is not sent with event is logged as "attribute not sent"
                var valueSent = (message[ruleIndex] == undefined) ? "attribute not sent" : message[ruleIndex];
                if (ruleApplied == "[ruleMismatch]") {
                    mismatched.push({ "attribute": ruleIndex, "value": valueSent, "rule": rule });
                } else if (ruleApplied == "[ruleError]") {
                    ruleError.push({ "attribute": ruleIndex, "value": valueSent, "rule": rule });
                }
            }

            var errorLogged = null;
            //Only log valid error cases
            if ((mismatched.length > 0) || (ruleError.length > 0)) {
                errorLogged = {
                    "level": "error",
                    "type": { "ruleMismatch": mismatched, "ruleError": ruleError }
                };
            }

            return { "error": errorLogged, "message": cleanMessage };
        }
        catch (e) {
            logError('Exception thrown while calling _applyAttributeRules: ' + e.message);
        }
        return { "message": {} };
    };
    Transmitter.prototype._transportsQueue = { 'Normal': {}, 'Critical': {} };
    Transmitter.prototype._queueProcessingTimeoutId = null;
    Transmitter.prototype._queueEmpty = true;
    Transmitter.prototype._timeoutInterval = 30000;

    var analyticstransport = new Transmitter();
    return analyticstransport;
}

ModuleManager.registerFactory("transport", CreateAnalyticsTransport);
//export  {CreateAnalyticsTransport};
//globalEngine.FactoryMap["transport"] =  CreateAnalyticsTransport ;
